import { AbstractFormElement, ICustomFormElement } from "./Abstract";
import { Reveal } from "../Dialog/Reveal";

export interface IRadioGroupOption {
    label: string;
    value: string;
    dataset?: { [key: string]: string };
}

class RadioGroup extends AbstractFormElement implements ICustomFormElement {
    declare radioTemplate: HTMLTemplateElement;
    declare infoTemplate: HTMLTemplateElement;

    // private radios: Radio[] = [];
    private radios: HTMLSpanElement[] = [];
    protected container: HTMLDivElement;

    private radioClickHandler: () => void;

    /**
     * @inheritDoc
     */
    initElements() {
        this.radioClickHandler = this.handleRadioClick.bind(this);
        this.container = this.querySelector<HTMLDivElement>(":scope > div");
        this.container.querySelector<HTMLSpanElement>(":scope > div.label").innerText = this.dataset.label;
        if (this.dataset.help) {
            this.querySelector<HTMLElement>("span.help").innerText = this.dataset.help;
        }
        if (this.dataset.info) {
            this.initInfo();
        }
        try {
            const options = JSON.parse(this.dataset.options) as IRadioGroupOption[];
            options.forEach((o) => this.initOption(o));
        } catch (error) {}
        if (this.hasAttribute("value")) {
            this.value = this.getAttribute("value");
        }

        return this;
    }

    /**
     * @inheritDoc
     */
    initAttributes() {
        this.required = this.hasAttribute("required");
        return this;
    }

    /**
     * @inheritdoc
     */
    attachEvents() {
        this.internals.form.addEventListener("submit", () => {
            this.container.classList.add("validates");
            this.validate();
        });
        if (this.dataset.info) {
            this.querySelector<HTMLElement>("span.info").addEventListener("click", () => {
                const label = this.dataset.title || this.dataset.label;
                const reveal = new Reveal(this.dataset.info, label);
                reveal.render();
            });
        }
        return this;
    }

    handleRadioClick(e: Event) {
        const radio = e.currentTarget as HTMLSpanElement;
        if (!this.container.classList.contains("validates")) {
            this.container.classList.add("validates");
        }
        this.radios.filter((r) => r !== radio).forEach((r: HTMLSpanElement) => r.classList.remove("checked"));
        radio.classList.add("checked");
        this.validate();
        this.dispatchEvent(new Event("change", e));
    }

    /**
     * initialize single option
     * @param {Object.<String: label, String: value>} option
     */
    initOption(option: IRadioGroupOption): void {
        const template = this.radioTemplate.content.cloneNode(true) as DocumentFragment;
        const radio = template.querySelector<HTMLSpanElement>(".radio");
        const label = template.querySelector<HTMLSpanElement>(".label");
        label.innerText = option.label;
        radio.dataset.value = option.value;
        if (option.dataset) {
            Object.keys(option.dataset).forEach((k) => (radio.dataset[k] = option.dataset[k]));
        }
        this.radios.push(radio);
        this.container.appendChild(radio);
        radio.addEventListener("click", this.radioClickHandler);
    }

    /**
     * remove option by value
     * @param v
     */
    removeOption(v: string): void {
        const radio = this.radios.find((r) => r.dataset.value === v);
        const idx = this.radios.findIndex((r) => r.dataset.value === v);
        radio.removeEventListener("click", this.radioClickHandler);
        radio.remove();
        this.radios.splice(idx, 1);
    }

    initInfo() {
        const info = this.infoTemplate.content.cloneNode(true) as DocumentFragment;
        this.container.querySelector<HTMLSpanElement>(":scope > div.label").appendChild(info);
        const label = this.container.querySelector<HTMLSpanElement>(":scope > div.label");
        label?.classList.add("has-info");
    }

    /**
     * @inheritDoc
     */
    validate() {
        this.internals.setFormValue(this.value);
        const checked = this.radios.find((r) => r.classList.contains("checked"));
        const validity: { customError?: boolean } = {};
        let message = "";
        this.internals.setValidity(validity, message, this.container);
        if (this.required && !checked) {
            validity.customError = true;
            message = this.matchValidityMessage();
            this.internals.setValidity(validity, message, this.container);
        }

        this.querySelector<HTMLSpanElement>(":scope > span.error").innerText = message;
        this.classList.toggle("valid", this.internals.validity.valid);
        return this.internals.validity.valid;
    }

    resetValidationState() {
        this.container.classList.remove("validates");
    }

    /**
     * @inheritDoc
     */
    matchValidityMessage() {
        let message;
        // obtain message from attribute
        try {
            message = JSON.parse(this.dataset.messages).valueMissing;
        } catch (error) {}

        // obtain message by tag name
        if (!message && window.ValidityMessages[this.tagName]) {
            message = window.ValidityMessages[this.tagName].valueMissing;
        }

        // obtain default message
        if (!message && window.ValidityMessages.default) {
            message = window.ValidityMessages.default.valueMissing;
        }
        return message;
    }

    get selectedIndex(): number {
        return this.radios.findIndex((r) => r.classList.contains("checked"));
    }

    get value(): string {
        return this.radios.find((r) => r.classList.contains("checked"))?.dataset.value;
    }

    set value(v) {
        const radio = this.radios.find((r) => r.dataset.value === v);
        this.radios.forEach((r) => r.classList.remove("checked"));
        if (radio) {
            radio.classList.add("checked");
        }
        this.validate();
    }

    get required() {
        return this.hasAttribute("required");
    }

    set required(v: boolean) {
        if (!v) {
            this.removeAttribute("required");
        } else if (!this.hasAttribute("required")) {
            this.setAttribute("required", v.toString());
        }
        this.validate();
    }

    get disabled() {
        return this.hasAttribute("disabled");
    }

    set disabled(v: boolean) {
        if (!v) {
            this.removeAttribute("disabled");
        } else if (!this.hasAttribute("disabled")) {
            this.setAttribute("disabled", v.toString());
        }
        this.validate();
    }

    get readonly() {
        return this.hasAttribute("readonly");
    }

    set readonly(v: boolean) {
        if (!v) {
            this.removeAttribute("readonly");
        } else if (!this.hasAttribute("readonly")) {
            this.setAttribute("readonly", v.toString());
        }
        this.validate();
    }

    get validity() {
        return this.internals.validity;
    }

    get willValidate() {
        return this.required;
    }

    /**
     * obtain options array
     */
    get options(): IRadioGroupOption[] {
        return this.radios.map((o) => {
            const dataset = { ...o.dataset };
            delete dataset.value;
            return {
                label: o.querySelector(".label").innerHTML,
                value: o.dataset.value,
                dataset
            };
        });
    }
}

RadioGroup.prototype.radioTemplate = document.createElement("template");
RadioGroup.prototype.radioTemplate.innerHTML = /*html*/ `
<div class="radio">
    <span class="radio-btn">
        <span class="icon radio-btn keep-svg"></span>
        <span class="icon radio-btn keep-svg active"></span>
    </span>
    <span class="label"></span>
</div>
`;

RadioGroup.prototype.infoTemplate = document.createElement("template");
RadioGroup.prototype.infoTemplate.innerHTML = /*html*/ `
<span class="icon info"></span>
`;

RadioGroup.prototype.template = document.createElement("template");
RadioGroup.prototype.template.innerHTML = /*html*/ `
<div class="std-fe">
    <div class="label"></div>
</div>
<span class="error"></span>
<span class="help"></span>
`;

customElements.define("x-radio-group", RadioGroup);

export { RadioGroup };
